import React, { useState } from 'react';
import { Eye, EyeOff, Shield, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENABLE_SIGNUP = false; // Toggle for "Create account" link
const FORGOT_PASSWORD_ROUTE = '/admin/support'; // Configurable route

/**
 * Caps Lock Indicator Pill
 */
const CapsLockPill = ({ isCapsLock }) => {
  if (!isCapsLock) return null;
  
  return (
    <div className="absolute right-12 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-yellow-700 bg-yellow-50 px-2 py-1 rounded-md border border-yellow-200">
      <AlertCircle size={12} />
      <span className="font-medium">Caps Lock is ON</span>
    </div>
  );
};

/**
 * Right Panel - Authentication Form
 */
const AuthForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Real-time validation
  const validateField = (name, value) => {
    const errors = {};
    
    if (name === 'identifier') {
      if (!value) {
        errors.identifier = 'Email or username is required';
      } else if (value.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors.identifier = 'Please enter a valid email address';
      }
    }
    
    if (name === 'password') {
      if (!value) {
        errors.password = 'Password is required';
      } else if (value.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
    }
    
    return errors;
  };

  // Handle input change with real-time validation
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));
    
    // Real-time validation
    if (type !== 'checkbox') {
      const fieldErrors = validateField(name, fieldValue);
      
      // Update validation errors - remove the field if no error, otherwise set the error
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        
        if (Object.keys(fieldErrors).length === 0) {
          // No errors for this field - remove it from validation errors
          delete newErrors[name];
        } else {
          // Set the error for this field
          Object.assign(newErrors, fieldErrors);
        }
        
        return newErrors;
      });
    }
  };

  // Detect Caps Lock
  const handleKeyUp = (e) => {
    setCapsLock(e.getModifierState('CapsLock'));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields
    const identifierErrors = validateField('identifier', formData.identifier);
    const passwordErrors = validateField('password', formData.password);
    const allErrors = { ...identifierErrors, ...passwordErrors };
    
    if (Object.keys(allErrors).length > 0) {
      setValidationErrors(allErrors);
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        identifier: formData.identifier,
        password: formData.password,
        remember_me: formData.rememberMe
      });

      // Check for MFA requirement
      if (response.data.mfa_required) {
        // Store challenge for MFA page
        sessionStorage.setItem('mfa_challenge', response.data.challenge_id);
        
        // Extract last 4 digits from message if available
        const mobileMatch = response.data.message?.match(/\d{4}$/);
        if (mobileMatch) {
          sessionStorage.setItem('mfa_mobile', mobileMatch[0]);
        }
        
        navigate('/mfa');
        return;
      }

      // Regular login success
      if (response.data.token || response.data.access_token) {
        const token = response.data.token || response.data.access_token;
        const userData = response.data.user || { email: formData.identifier };
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        login(userData, token);
        navigate('/');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 
                      err.response?.data?.message || 
                      'Login failed. Please check your credentials.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col justify-center gap-4 px-6">
      {/* Login Form - Top */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 w-full max-w-xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h2>
          <p className="text-xs text-gray-600">
            Enter your credentials to access URGAA platform
          </p>
        </div>

        {/* MFA Notice */}
        <div className="mb-4 p-2.5 bg-blue-50 rounded-xl flex items-start gap-2 text-xs border border-blue-100">
          <Shield className="text-blue-600 flex-shrink-0 mt-0.5" size={14} />
          <div className="text-blue-800">
            <strong>MFA available</strong> — use your authenticator when prompted.
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Email or Username */}
          <div>
            <label 
              htmlFor="identifier" 
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Email or Username
            </label>
            <input
              type="text"
              id="identifier"
              name="identifier"
              value={formData.identifier}
              onChange={handleChange}
              className={`w-full px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-[#0DA34E] focus:border-transparent outline-none transition-all ${
                validationErrors.identifier ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="you@go4garage.in"
              autoComplete="username"
              aria-invalid={!!validationErrors.identifier}
              aria-describedby={validationErrors.identifier ? 'identifier-error' : undefined}
            />
            {validationErrors.identifier && (
              <p id="identifier-error" className="mt-1 text-[10px] text-red-600 flex items-center gap-1">
                <AlertCircle size={10} />
                {validationErrors.identifier}
              </p>
            )}
            {!validationErrors.identifier && formData.identifier && (
              <p className="mt-1 text-[10px] text-green-600 flex items-center gap-1">
                <CheckCircle2 size={10} />
                Valid email or username
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label 
              htmlFor="password" 
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
                className={`w-full px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-[#0DA34E] focus:border-transparent outline-none transition-all pr-20 ${
                  validationErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="••••••••"
                autoComplete="current-password"
                aria-invalid={!!validationErrors.password}
                aria-describedby={validationErrors.password ? 'password-error' : undefined}
              />
              
              {/* Caps Lock Indicator */}
              <CapsLockPill isCapsLock={capsLock} />
              
              {/* Show/Hide Password */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {validationErrors.password && (
              <p id="password-error" className="mt-1 text-[10px] text-red-600 flex items-center gap-1">
                <AlertCircle size={10} />
                {validationErrors.password}
              </p>
            )}
            
            <p className="mt-1 text-[10px] text-gray-500">
              Use 8+ characters with a number and a capital letter.
            </p>
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="w-3.5 h-3.5 rounded border-gray-300 text-[#570683] focus:ring-[#0DA34E] transition-colors"
              />
              <span>Remember me</span>
            </label>

            <a
              href={FORGOT_PASSWORD_ROUTE}
              className="text-xs text-[#570683] hover:text-[#417E46] hover:underline transition-colors"
            >
              Forgot password?
            </a>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading || Object.values(validationErrors).some(error => error !== undefined && error !== null)}
            className="w-full py-2.5 text-sm bg-gradient-to-r from-[#570683] to-[#417E46] text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              'Sign In'
            )}
          </button>

          {/* Optional Create Account Link */}
          {ENABLE_SIGNUP && (
            <div className="text-center">
              <a
                href="/signup"
                className="text-xs text-[#570683] hover:underline"
              >
                Create account
              </a>
            </div>
          )}
        </form>

        {/* Security Notice */}
        <div className="mt-4 p-2.5 bg-gray-50 rounded-xl text-[10px] text-gray-600 border border-gray-100">
          <p className="flex items-center gap-2">
            <Shield size={12} className="text-green-600" />
            <span>
              Passwords are encrypted; sessions use HTTP-only cookies.
            </span>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
          <span>Made in Bharat</span>
          <span className="text-base">🇮🇳</span>
        </div>
      </div>

      {/* Video Section - Below Form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 overflow-hidden w-full max-w-xl mx-auto">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-auto rounded-lg"
        >
          <source src="/assets/Heading.mp4" type="video/mp4" />
          <p className="text-sm text-gray-500 text-center py-4">Your browser does not support the video tag.</p>
        </video>
      </div>
    </div>
  );
};

export default AuthForm;
